import React, {useEffect, useMemo} from "react";
import {useQuery} from "@apollo/client";
import gql from "graphql-tag";
import {useHistory} from "react-router";
import ReactDocumentTitle from "../../../components/ReactDocumentTitle";
import AppLayout from "../../../layouts/AppLayout";
import {Attendee, License, Licensee, LicenseeOrderByInput, LicenseeType} from "../../../schema";
import {SortingRule} from "react-table";
import Table, {TableFilter} from "../../../ui/table/Table";
import H1 from "../../../ui/H1";
import {reverse} from "../../../urls";
import {FilterState} from "../../../ui/table/TableFilter";
import Countries from "../../../models/utils/Countries";
import DateTimeFormatter from "../../../models/formatter/DateTimeFormatter";
import {DateTime} from "luxon";
import {RadioFilterBlock} from "../../../ui/table/filter/UIRadioFilterBlock";
import {OfficeBuildingIcon, UserIcon} from "@heroicons/react/outline";
import PrimaryButton from "../../../ui/PrimaryButton";
import {AccountContext} from "../../../utils/Context";
import DropdownActionMenu from "../../../ui/DropdownActionMenu";
import DropdownMenuItem from "../../../ui/DropdownMenuItem";

type TableData = {
  id: string
  name: JSX.Element
  country: string
  startDate: string
  expirationDate: string
}

function fromLicense(license: License): TableData {
  const isCompany = license.licensee.type == LicenseeType.Company;
  const name = isCompany ?
    <><OfficeBuildingIcon className="text-gray-dark w-4 h-4 inline mr-2 mb-1"/>{license.licensee.name}</> :
    <><UserIcon className="text-gray-dark w-4 h-4 inline  mr-2  mb-1"/>{license.licensee.name}</>;
  return {
    id: license.licensee.id,
    name,
    country: Countries.getName(license.licensee.address.country),
    startDate: DateTimeFormatter.format(navigator.language, DateTime.fromISO(license.periods.map(x => x.start)[0])),
    expirationDate: DateTimeFormatter.format(navigator.language, DateTime.fromISO(license.expiresAt))
  }
}

export default function BrandLicenseeListScene() {
  const history = useHistory();

  const skipPageResetRef = React.useRef<boolean>();
  const [licensees, setLicensees] = React.useState<TableData[]>([]);
  const [totalCount, setTotalCount] = React.useState<number>(1);
  const [skip, setSkip] = React.useState<number>(0);
  const [first, setFirst] = React.useState<number>(25);
  const [sortBy, setSortBy] = React.useState<LicenseeOrderByInput>(LicenseeOrderByInput.name_asc);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(undefined);
  const [skipDataLoad, setSkipDataLoad] = React.useState<boolean>(true);

  const [states, setStates] = React.useState<FilterState[]>([new FilterState('licenseeType', false, ''),
    new FilterState('status', true, 'active')]);
  const selectedTypeId = states.find(x => x.id === 'licenseeType' && x.active)?.value;
  const selectedStatus = states.find(x => x.id === 'status' && x.active)?.value;

  const {data, loading, error} = useQuery<LicenseeQuery>(query, {
    skip: skipDataLoad,
    fetchPolicy: 'cache-and-network',
    variables: {skip, first, sortBy, searchQuery, filterByType: selectedTypeId, filterByStatus: selectedStatus},
  });

  useMemo(() => {
    if (data) {
      setLicensees(data.feed.licensees.map(x => fromLicense(x)))
      setTotalCount(data.feed.total);
    }
  }, [data])

  const onFetchData = (pageIndex: number, newPageSize: number, newSortBy: SortingRule<Attendee>[], filter: TableFilter | null) => {
    skipPageResetRef.current = true;
    setSkip(pageIndex * newPageSize);
    setFirst(newPageSize);
    let sortByValue = LicenseeOrderByInput.name_asc;
    if (newSortBy.length > 0) {
      switch (newSortBy[0]?.id) {
        case 'country': {
          sortByValue = newSortBy[0]?.desc ? LicenseeOrderByInput.country_desc : LicenseeOrderByInput.country_asc;
          break;
        }
        case 'startDate': {
          sortByValue = newSortBy[0]?.desc ? LicenseeOrderByInput.joined_desc : LicenseeOrderByInput.joined_asc;
          break;
        }
        case 'expirationDate': {
          sortByValue = newSortBy[0]?.desc ? LicenseeOrderByInput.expired_at_desc : LicenseeOrderByInput.expired_at_asc;
          break;
        }
        default:
          sortByValue = newSortBy[0]?.desc ? LicenseeOrderByInput.name_desc : LicenseeOrderByInput.name_asc;
      }
    }
    setSearchQuery(filter?.search);
    setSortBy(sortByValue);
    setStates(filter?.filters || []);
    setSkipDataLoad(false);
  }

  const onClick = (row: TableData) => {
    history.push(reverse('licensee_profile', {id: row.id}));
  }

  useEffect(() => {
    skipPageResetRef.current = false;
  }, []);

  const typeFilter = new RadioFilterBlock('licenseeType', 'Licensee', 'type', [{
    id: 'company',
    value: 'company',
    label: 'Company',
    checked: false
  }, {id: 'trainer', value: 'trainer', label: 'Trainer', checked: false}]);
  const statusFilter = new RadioFilterBlock('status', 'Status', 'status', [{
    id: 'active',
    value: 'active',
    label: 'Active',
    checked: true
  }, {id: 'expired', value: 'expired', label: 'Expired', checked: false}])

  const columns = React.useMemo(() => [
    {
      Header: 'Licensees',
      columns: [
        {
          Header: 'Name',
          accessor: 'name',
          filter: 'fuzzyText',
          defaultSort: 'desc',
          sortType: 'basic',

        },
        {
          Header: 'Country',
          accessor: 'country',
        },
        {
          Header: 'Joined',
          accessor: 'startDate',
        },
        {
          Header: 'License expires',
          accessor: 'expirationDate',
        }
      ]
    }
  ].flatMap((c: any) => c.columns), []);

  return (
    <>
      <ReactDocumentTitle title='Facilitators'/>

      <AppLayout className="">
        <div className="pt-8 pb-20">
          <div className="flex justify-between">
            <H1>Facilitators</H1>
            <AccountContext.Consumer>
              {account => (<>
                {account?.id == 1 &&
                  <DropdownActionMenu
                    narrow={true}
                    caption="Add facilitator"
                    onButtonClick={() => window.location.assign('/brand/trainer/new')}
                  >
                    <DropdownMenuItem onClick={() => history.push(reverse('licensees_add_company'))}
                                      caption="Add company">
                      <OfficeBuildingIcon className="text-gray-dark w-4 h-4 mr-2 inline"/>
                    </DropdownMenuItem>
                  </DropdownActionMenu>
                }
                {account?.id != 1 &&
                  <PrimaryButton onClick={() => window.location.assign('/brand/trainer/new')}>Add
                    facilitator</PrimaryButton>
                }
              </>)}
            </AccountContext.Consumer>
          </div>
          <div className="mt-6 mb-4">
            <Table<TableData, TableFilter> name="licensees" reset={skipPageResetRef.current || false}
                                           error={error?.message}
                                           loading={loading}
                                           total={totalCount}
                                           onFetchData={onFetchData} columns={columns}
                                           data={licensees}
                                           initialFilterState={states}
                                           filters={[typeFilter, statusFilter]}
                                           onClick={onClick}/>
          </div>
        </div>
      </AppLayout>
    </>
  )
}

type LicenseeFeed = {
  total: number;
  count: number;
  skip: number;
  licensees: License[];
}
type LicenseeQuery = {
  feed: LicenseeFeed;
}


const query = gql`
  query LicenseeListQuery($searchQuery: String, $skip: Int!, $first: Int!, $sortBy: LicenseeOrderByInput!, $filterByType: String, $filterByStatus: String) {
    feed: licensees(searchBy: $searchQuery, orderBy: $sortBy, skip: $skip, count: $first, filterByType: $filterByType, filterByStatus: $filterByStatus) {
      total
      count
      skip

      licensees {
        id
        expiresAt
        active
        periods {
          start
        }
        licensee {
          id
          name
          type
          address {
            country
          }
        }
      }
    }
  }
`;
